// Shared sentiment-polarity primitives for LensesV2 — the SOURCE of truth for how
// a tag maps to positive/negative/neutral. Used by the Sentiment widget (overall)
// and the Feature widget (per-feature health), so they agree on polarity.
//
// Two derivation sources (same pattern in both widgets, each picks its own,
// defaulted to emotion/affect):
//   "emotionAffect" → emotion & affect tagId → polarity (EMOTION_POLARITY)
//   "signals"       → signal user-lens category → polarity (CATEGORY_POLARITY)

import { colors2 } from "oolib";

export const POSITIVE = "positive";
export const NEGATIVE = "negative";
export const NEUTRAL = "neutral";

// Where polarity is derived from, dashboard-wide — a single decision shared by the
// Sentiment widget (overall) and the Feature widget (per-feature health), so they
// never disagree. Flip here to switch BOTH.
//   "emotionAffect" → emotion & affect tags (EMOTION_POLARITY)
//   "signals"       → signal user-lens category (CATEGORY_POLARITY)
export const SENTIMENT_SOURCE = "emotionAffect";

// signal user-lens category → polarity. Unlisted → neutral.
export const CATEGORY_POLARITY = {
  userSatisfaction: POSITIVE,
  userDissatisfaction: NEGATIVE,
  userNeeds: NEUTRAL,
  userBehaviour: NEUTRAL,
};

// fixed emotion tagId → polarity. Covers ALL 13 Emotion & Affect tags from the
// base taxonomy (attributeCategories.js). Anything unlisted falls to neutral.
export const EMOTION_POLARITY = {
  emotion_affect_joy: POSITIVE,
  emotion_affect_relief: POSITIVE,
  emotion_affect_enthusiasm: POSITIVE,
  emotion_affect_frustration: NEGATIVE,
  emotion_affect_anger: NEGATIVE,
  emotion_affect_confusion: NEGATIVE,
  emotion_affect_fear: NEGATIVE,
  emotion_affect_disgust: NEGATIVE,
  emotion_affect_sadness: NEGATIVE,
  emotion_affect_skepticism: NEGATIVE, // doubt / distrust
  emotion_affect_urgency: NEUTRAL, // time pressure / activation — not inherently negative
  emotion_affect_surprise: NEUTRAL, // ambiguous polarity — neutral by default
  emotion_affect_neutral: NEUTRAL,
};

// Build a tag `_id` → polarity lookup for a source, joining the context tag lists
// (cooccurrence pairs carry only `_id`, so we resolve polarity by joining to these).
//   emotionAffect: [{ _id, tagId }]  signals: [{ _id, category }]
export const buildPolarityById = (source, { signals = [], emotionAffect = [] } = {}) => {
  const map = {};
  if (source === "emotionAffect") {
    for (const e of emotionAffect) map[String(e._id)] = EMOTION_POLARITY[e.tagId] || NEUTRAL;
  } else {
    for (const s of signals) map[String(s._id)] = CATEGORY_POLARITY[s.category] || NEUTRAL;
  }
  return map;
};

// A sentiment breakdown { positive, negative, neutral } → a status-pill theme.
// health = (positive − negative) / classified. Same v1 HEALTH_THEME tokens the
// Feature widget used — shared so Feature + Segment pills look identical. No
// classified sentiment (total 0) → Neutral.
export const sentimentPillTheme = ({ health = 0, total = 0 } = {}) => {
  if (total && health > 0.15) return { bg: "#6BDC58", color: colors2.grey80, label: "Positive" };
  if (total && health < -0.15) return { bg: "#FF7F7F", color: colors2.grey80, label: "Negative" };
  return { bg: "#03143033", color: colors2.grey70, label: "Neutral" };
};
